/* GrowthOS — Lead form sections (used by addLead.jsx for Create/Edit Lead).
   Redesigned to design growthos/CreateLead: section cards in a 2-col grid + notes.
   Keeps the exact handler contract from addLead (handleChangeForm / handleChange /
   handleChangeCheckbox / errormsg / refs / selectedOptions). */
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getActiveOwners, getGenericMasterFilteredListByKey, getGeographyNodes, getOrgUnitNodes } from '../../redux/selector';
import { NameFormatter } from '../../shared/controls/formatters';
import { FIc } from "../../shared/component/GosIcon";

const ACCESS_LEVEL_OPTIONS = [
  { value: 'L1', label: 'L1' },
  { value: 'L2', label: 'L2' },
  { value: 'L3', label: 'L3' },
];

// Maps a GeographyNode/OrgUnitNode API record ({ id, nodeType, parentId, name, path }) to a select option
const toScopeNodeOption = (node) => ({ value: node.id, label: `${node.nodeType} - ${node.name} - ${node.path}` });




/* GrowthOS select (reuses .gos-dd-* dropdown styles) */
const Select = ({ value, placeholder, options, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  return (
    <div className="gos-dd" ref={ref}>
      <button type="button" className={'gos-dd-btn' + (open ? ' open' : '') + (value ? ' has-val' : '')} onClick={() => !disabled && setOpen((o) => !o)} disabled={disabled}>
        <span className="gos-dd-label">{value || placeholder || 'Select…'}</span>
        <FIc name="chevD" size={14} style={{ flex: 'none', color: 'var(--gos-text-3)' }} />
      </button>
      {open ? (
        <div className="gos-dd-pop">
          <div className="gos-dd-list">
            {options.length === 0 ? <div className="gos-dd-empty">No options</div> : null}
            {options.map((o) => (
              <button key={o.value} type="button" className={'gos-dd-item' + (o.label === value ? ' on' : '')} onClick={() => { onChange(o); setOpen(false); }}>
                <span className="gos-dd-grow">{o.label}</span>
                {o.label === value ? <FIc name="check" size={14} style={{ color: 'var(--gos-accent)' }} /> : null}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const LeadForm = ({ formData, handleChangeForm, handleChange, errormsg, handleChangeCheckbox, refs }) => {
  const owner = useSelector(getActiveOwners);
  const revenueTypeDropdown = useSelector(getGenericMasterFilteredListByKey('RevenueType'));
  const industryTypeDropdown = useSelector(getGenericMasterFilteredListByKey('Industry'));
  const leadSourceDropdown = useSelector(getGenericMasterFilteredListByKey('LeadSource'));
  const timeZone = useSelector(getGenericMasterFilteredListByKey('TimeZone'));
  const salutation = useSelector(getGenericMasterFilteredListByKey('Salutation'));
  const geographyNodes = useSelector(getGeographyNodes);
  const orgUnitNodes = useSelector(getOrgUnitNodes);

  const opt = (list, vKey, lKey, fmt) =>
    (list || []).map((o) => ({ value: o[vKey], label: fmt ? fmt(o[lKey]) : o[lKey] }));

  const geoScopeOptions = (geographyNodes || []).map(toScopeNodeOption);
  const orgScopeOptions = (orgUnitNodes || []).map(toScopeNodeOption);

  // field config: text | select | textarea
  const SECTIONS = [
    {
      id: 'company', icon: 'building', title: 'Company Information', sub: 'Firmographic details',
      fields: [
        { label: 'Company Name', name: 'companyName', req: true, full: true, ref: 'companyName' },
        { label: 'Phone', name: 'phone' },
        { label: 'Alternate Phone', name: 'alternatePhone', ph: 'Enter Phone Number 2' },
        { label: 'Time Zone', name: 'timeZoneId', type: 'select', display: 'timeZone', options: opt(timeZone, 'id', 'displayName') },
        { label: 'Company LinkedIn', name: 'companyLinkedIn' },
        { label: 'Website', name: 'website', ph: 'Enter Company Website' },
        { label: 'Annual Revenue', name: 'annualRevenue' },
        { label: 'Written Premium', name: 'writtenPremium' },
        { label: 'Revenue Type', name: 'revenueTypeId', type: 'select', display: 'revenueType', options: opt(revenueTypeDropdown, 'id', 'displayName') },
        { label: 'No. of Employees', name: 'noOfEmployees' },
        { label: 'OrgUnitNode', name: 'orgScopeNodeId', type: 'select', display: 'orgScopeNode', req: true, ref: 'orgScopeNodeId', full: true, options: orgScopeOptions },
      ],
    },
    {
      id: 'general', icon: 'note', title: 'General Information', sub: 'Source & ownership',
      fields: [
        { label: 'Business Type', name: 'businessType' },
        { label: 'Industry', name: 'industryId', type: 'select', display: 'industry', req: true, ref: 'industryId', options: opt(industryTypeDropdown, 'id', 'displayName') },
        ...(formData?.industry === 'Healthcare' ? [{ label: 'No. of Beds', name: 'noOfBeds' }] : []),
        { label: 'Line of Business', name: 'lineOfBusiness', full: true },
        { label: 'Lead Source', name: 'leadSourceId', type: 'select', display: 'leadSource', req: true, ref: 'leadSourceId', options: opt(leadSourceDropdown, 'id', 'displayName') },
        { label: 'Source Name', name: 'sourceName', req: true, ref: 'sourceName' },
        { label: 'Other Source Name', name: 'otherSourceName', full: true },
        { label: 'Lead Owner', name: 'ownerId', type: 'select', display: 'owner', options: opt(owner, 'userId', 'display_name', NameFormatter) },
        { label: 'Access Level', name: 'accessLevel', type: 'select', display: 'accessLevel', options: ACCESS_LEVEL_OPTIONS },
      ],
    },
    {
      id: 'contact', icon: 'contacts', title: 'Contact Information', sub: 'Primary point of contact', optout: true,
      fields: [
        { label: 'Salutation', name: 'salutationId', type: 'select', display: 'salutation', options: opt(salutation, 'id', 'displayName') },
        { label: 'First Name', name: 'firstName' },
        { label: 'Middle Name', name: 'middleName' },
        { label: 'Last Name', name: 'lastName', req: true, ref: 'lastName' },
        { label: 'Title', name: 'title', full: true },
        { label: 'Email', name: 'email' },
        { label: 'Secondary Email', name: 'secondaryEmail' },
        { label: 'Direct Phone', name: 'directPhone' },
        { label: 'Mobile', name: 'mobile' },
        { label: 'Contact LinkedIn', name: 'contactLinkedIn', full: true },
      ],
    },
    {
      id: 'address', icon: 'location', title: 'Address Information', sub: 'Mailing & billing location',
      fields: [
        { label: 'Street', name: 'street', full: true },
        { label: 'City', name: 'city' },
        { label: 'State', name: 'state' },
        { label: 'Zip Code', name: 'zipCode' },
        { label: 'Country', name: 'country' },
        { label: 'GeographyNode', name: 'geoScopeNodeId', type: 'select', display: 'geoScopeNode', req: true, ref: 'geoScopeNodeId', full: true, options: geoScopeOptions },
      ],
    },
    {
      id: 'notes', icon: 'template', title: 'Notes & Tech Details', sub: 'Context for the sales team',
      fields: [
        { label: 'Description', name: 'description', type: 'textarea', full: true },
        { label: 'Technology Details', name: 'technologyDetails', type: 'textarea', full: true },
      ],
    },
  ];

  const renderField = (fl) => {
    const err = errormsg?.[fl.name];
    let control;
    if (fl.type === 'select') {
      // Falls back to an options lookup by id when the display label isn't already on formData
      // (e.g. editing a lead whose saved record only carries the scope node id, not its composed label).
      const displayValue =
        formData?.[fl.display] ||
        (fl.options || []).find((o) => String(o.value) === String(formData?.[fl.name]))?.label ||
        '';
      control = (
        <Select
          value={displayValue}
          placeholder={'Select ' + fl.label}
          options={fl.options}
          onChange={(o) => handleChange(o, fl.name)}
        />
      );
    } else if (fl.type === 'textarea') {
      control = (
        <textarea
          className={'gos-cl-input gos-cl-textarea' + (err ? ' err' : '')}
          name={fl.name}
          rows={4}
          placeholder={fl.ph || 'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    } else {
      control = (
        <input
          className={'gos-cl-input' + (err ? ' err' : '')}
          name={fl.name}
          placeholder={fl.ph || 'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    }
    return (
      <div className={'gos-cl-field' + (fl.full ? ' gos-cl-field-full' : '')} key={fl.name} ref={fl.ref ? refs?.[fl.ref] : undefined}>
        <label className="gos-cl-label">
          {fl.label}
          {fl.req ? <span className="gos-cl-req">*</span> : null}
        </label>
        {control}
        {err ? <span className="gos-cl-err">{err}</span> : null}
      </div>
    );
  };

  const secCount = (s) => s.fields.filter((fl) => { const v = formData?.[fl.name]; return v !== null && v !== undefined && String(v).trim() !== ''; }).length;

  const optedOut = !!formData?.emailOptOut;
  const optoutRow = (
    <div className="gos-cl-optrow" key="optout">
      <span className="gos-cl-optrow-ic" style={optedOut ? { background: 'var(--gos-amber-bg)', color: 'var(--gos-amber-fg)' } : null}>
        <FIc name="mail" size={16} />
      </span>
      <div className="gos-cl-optrow-meta">
        <div className="gos-cl-optrow-title">Email Opt Out</div>
        <div className="gos-cl-optrow-sub">{optedOut ? 'Excluded from email campaigns' : 'Receives marketing & sales emails'}</div>
      </div>
      <button
        type="button"
        className={'gos-cl-toggle' + (optedOut ? ' on' : '')}
        onClick={() => handleChangeCheckbox({ target: { name: 'emailOptOut', checked: !optedOut } })}
        aria-pressed={optedOut}
      >
        <span className="gos-cl-toggle-knob" />
      </button>
    </div>
  );

  const section = (s) => {
    const n = secCount(s);
    const total = s.fields.length;
    return (
      <div className="gos-lr-card gos-cl-sec" id={'sec-' + s.id} key={s.id}>
        <div className="gos-cl-sec-head">
          <span className="gos-cl-sec-ic"><FIc name={s.icon} size={17} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="gos-cl-sec-title">{s.title}</div>
            <div className="gos-cl-sec-sub">{s.sub}</div>
          </div>
          <span className={'gos-cl-count' + (n === total && n > 0 ? ' done' : '')}>{n}/{total}</span>
        </div>
        <div className="gos-cl-sec-body">
          {s.fields.map(renderField)}
          {s.optout ? optoutRow : null}
        </div>
      </div>
    );
  };

  const byId = (id) => SECTIONS.find((s) => s.id === id);

  return (
    <>
      <div className="gos-cl3-grid">
        <div className="gos-cl3-col">{section(byId('company'))}{section(byId('general'))}</div>
        <div className="gos-cl3-col">{section(byId('contact'))}{section(byId('address'))}</div>
      </div>
      <div style={{ marginTop: 18 }}>{section(byId('notes'))}</div>
    </>
  );
};

export default LeadForm;
